import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Truck, Users, Zap, CreditCard, ArrowRight, Shield, Clock, CheckCircle, Megaphone } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const benefits = [
  {
    icon: Users,
    title: "Demandes qualifiées",
    description: "Recevez des demandes de clients sérieux, prêts à envoyer leurs colis.",
  },
  {
    icon: Zap,
    title: "Zéro prospection",
    description: "Fini la recherche de clients. Ils viennent directement à vous via la plateforme.",
  },
  {
    icon: CreditCard,
    title: "Inscription gratuite",
    description: "Rejoignez le réseau gratuitement et commencez à recevoir des demandes.",
  },
];

const CarrierCTASection = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-12 md:py-20 bg-gradient-to-br from-secondary/10 via-background to-primary/10">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div ref={leftRef} className={`transition-all duration-700 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <span className="inline-flex items-center gap-2 bg-secondary/20 text-secondary-foreground border border-secondary/40 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Truck className="w-4 h-4 text-foreground" />
              Pour les transporteurs
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Vous êtes{" "}
              <span className="text-foreground">transporteur ?</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6">
              Rejoignez notre réseau et recevez des demandes de clients
              qualifiés pour vos trajets France-Maroc. Simple, gratuit, efficace.
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`flex items-start gap-3 transition-all duration-500 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${200 + index * 100}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-0.5 text-sm">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`transition-all duration-500 ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '500ms' }}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                <Link to="/devenir-transporteur">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group text-base px-6 py-5 shadow-lg">
                    <Truck className="w-5 h-5 mr-2 text-secondary-foreground" />
                    Rejoindre GoBledPro
                    <ArrowRight className="w-5 h-5 ml-2 text-secondary-foreground group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/nouvelle-annonce-transporteur">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground group text-base px-6 py-5 shadow-lg">
                    <Megaphone className="w-5 h-5 mr-2" />
                    Publier une annonce
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right - Stats */}
          <div ref={rightRef} className="relative">
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: CreditCard, value: "0 €", label: "Inscription", bgColor: "bg-primary/10", borderColor: "border-primary/40", iconColor: "text-primary", valueColor: "text-primary" },
                { icon: Shield, value: "✓", label: "Vérification structurée", bgColor: "bg-primary/10", borderColor: "border-primary/40", iconColor: "text-primary", valueColor: "text-primary" },
                { icon: Clock, value: "Rapide", label: "Mise en relation", bgColor: "bg-primary/10", borderColor: "border-primary/40", iconColor: "text-primary", valueColor: "text-primary" },
                { icon: CheckCircle, value: "Simple", label: "Processus clair", bgColor: "bg-primary/10", borderColor: "border-primary/40", iconColor: "text-primary", valueColor: "text-primary" },
              ].map((stat, index) => (
                <div 
                  key={index}
                  className={`${stat.bgColor} border-2 ${stat.borderColor} rounded-xl p-5 text-center transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 hover:bg-primary/20 hover:border-primary/60 cursor-pointer ${rightVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                  style={{ transitionDelay: `${150 + index * 100}ms` }}
                >
                  <stat.icon className={`w-9 h-9 mx-auto mb-3 ${stat.iconColor}`} strokeWidth={2.5} />
                  <p className={`text-2xl md:text-3xl font-heading font-bold ${stat.valueColor} mb-1`}>{stat.value}</p>
                  <p className="text-foreground font-bold text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
            
            {/* Decorative */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-secondary/20 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CarrierCTASection;
